import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  User, 
  Calendar as CalendarIcon, 
  ClipboardCheck,
  ChevronRight,
  ChevronLeft,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM"
];

export default function AppointmentPage() {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    insurance_provider: "",
    insurance_id: "",
    service: "",
    doctor: "",
    preferred_date: null,
    preferred_time: "",
    reason: "",
    is_new_patient: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, doctorsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/doctors`),
        ]);
        setServices(servicesRes.data);
        setDoctors(doctorsRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, preferred_date: date }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      return formData.first_name && formData.last_name && formData.email && formData.phone && formData.date_of_birth;
    }
    if (stepNum === 2) {
      return formData.service && formData.preferred_date && formData.preferred_time;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!formData.reason) {
      toast.error("Please describe your reason for visit");
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, "yyyy-MM-dd") : "",
      };
      
      await axios.post(`${API}/appointments`, submitData);
      setIsComplete(true);
      toast.success("Appointment booked successfully!");
    } catch (err) {
      console.error("Error booking appointment:", err);
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <div data-testid="appointment-success" className="pt-20 min-h-screen flex items-center justify-center">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#00f0ff]/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
            <Check className="w-10 h-10 text-[#00f0ff]" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Appointment Confirmed!</h1>
          <p className="text-gray-400 mb-8">
            Thank you, {formData.first_name}! Your appointment has been scheduled for{" "}
            <span className="text-[#00f0ff]">
              {formData.preferred_date && format(formData.preferred_date, "MMMM d, yyyy")} at {formData.preferred_time}
            </span>
            . We've sent a confirmation email to {formData.email}.
          </p>
          <Button 
            onClick={() => window.location.href = "/"}
            className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] transition-all"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="appointment-page" className="pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="py-12 lg:py-16 miami-grid relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#00f0ff]/10 rounded-full blur-[150px]" />
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Schedule a Visit</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Book Your <span className="text-gradient">Appointment</span>
            </h1>
            <p className="text-gray-400">Schedule your visit in just a few simple steps</p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[
              { num: 1, icon: User, label: "Personal Info" },
              { num: 2, icon: CalendarIcon, label: "Schedule" },
              { num: 3, icon: ClipboardCheck, label: "Review" },
            ].map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className={`step ${step >= s.num ? (step > s.num ? 'completed' : 'active') : ''}`}>
                  <div className="step-circle">
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <span className={`hidden md:block ml-2 text-sm ${step >= s.num ? 'text-white' : 'text-gray-500'}`}>
                    {s.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`step-line mx-4 ${step > s.num ? 'completed' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {/* Form Card */}
          <div className="glass rounded-3xl p-8 lg:p-12">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div data-testid="step-1-personal-info" className="space-y-6 animate-fade-in-up">
                <h2 className="text-2xl font-bold text-white mb-6">Personal Information</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name" className="text-gray-300 mb-2 block">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      data-testid="input-first-name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="John"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-gray-300 mb-2 block">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      data-testid="input-last-name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-gray-300 mb-2 block">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="date_of_birth" className="text-gray-300 mb-2 block">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    data-testid="input-dob"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    className="bg-[#2a1b3d] border-[#4b0082] text-white focus:border-[#00f0ff]"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="insurance_provider" className="text-gray-300 mb-2 block">Insurance Provider</Label>
                    <Input
                      id="insurance_provider"
                      name="insurance_provider"
                      data-testid="input-insurance-provider"
                      value={formData.insurance_provider}
                      onChange={handleInputChange}
                      placeholder="Blue Cross Blue Shield"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="insurance_id" className="text-gray-300 mb-2 block">Insurance ID</Label>
                    <Input
                      id="insurance_id"
                      name="insurance_id"
                      data-testid="input-insurance-id"
                      value={formData.insurance_id}
                      onChange={handleInputChange}
                      placeholder="ABC123456789"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={nextStep}
                    data-testid="step-1-next"
                    className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] transition-all"
                  >
                    Next Step
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Appointment Details */}
            {step === 2 && (
              <div data-testid="step-2-appointment" className="space-y-6 animate-fade-in-up">
                <h2 className="text-2xl font-bold text-white mb-6">Appointment Details</h2>
                
                <div>
                  <Label className="text-gray-300 mb-2 block">Service Type *</Label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => handleSelectChange("service", value)}
                  >
                    <SelectTrigger 
                      data-testid="select-service"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a0b2e] border-[#4b0082]">
                      {services.map((service) => (
                        <SelectItem 
                          key={service.id} 
                          value={service.name}
                          className="text-white hover:bg-[#2a1b3d] focus:bg-[#2a1b3d]"
                        >
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Preferred Doctor (Optional)</Label>
                  <Select 
                    value={formData.doctor} 
                    onValueChange={(value) => handleSelectChange("doctor", value)}
                  >
                    <SelectTrigger 
                      data-testid="select-doctor"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white"
                    >
                      <SelectValue placeholder="Any available doctor" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a0b2e] border-[#4b0082]">
                      <SelectItem 
                        value="any"
                        className="text-white hover:bg-[#2a1b3d] focus:bg-[#2a1b3d]"
                      >
                        Any available doctor
                      </SelectItem>
                      {doctors.map((doctor) => (
                        <SelectItem 
                          key={doctor.id} 
                          value={doctor.name}
                          className="text-white hover:bg-[#2a1b3d] focus:bg-[#2a1b3d]"
                        >
                          {doctor.name} - {doctor.specialty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-300 mb-2 block">Preferred Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="date-picker-trigger"
                          className="w-full justify-start text-left font-normal bg-[#2a1b3d] border-[#4b0082] text-white hover:bg-[#3a2b4d] hover:text-white"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#00f0ff]" />
                          {formData.preferred_date ? (
                            format(formData.preferred_date, "PPP")
                          ) : (
                            <span className="text-gray-500">Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1a0b2e] border-[#4b0082]">
                        <Calendar
                          mode="single"
                          selected={formData.preferred_date}
                          onSelect={handleDateSelect}
                          disabled={(date) => date < new Date() || date.getDay() === 0}
                          className="bg-[#1a0b2e] text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-gray-300 mb-2 block">Preferred Time *</Label>
                    <Select 
                      value={formData.preferred_time} 
                      onValueChange={(value) => handleSelectChange("preferred_time", value)}
                    >
                      <SelectTrigger 
                        data-testid="select-time"
                        className="bg-[#2a1b3d] border-[#4b0082] text-white"
                      >
                        <SelectValue placeholder="Select a time" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a0b2e] border-[#4b0082] max-h-60">
                        {timeSlots.map((time) => (
                          <SelectItem 
                            key={time} 
                            value={time}
                            className="text-white hover:bg-[#2a1b3d] focus:bg-[#2a1b3d]"
                          >
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Are you a new patient?</Label>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      data-testid="new-patient-yes"
                      onClick={() => handleSelectChange("is_new_patient", true)}
                      className={`rounded-full px-6 ${formData.is_new_patient 
                        ? 'bg-[#00f0ff] text-black' 
                        : 'bg-[#2a1b3d] text-white border border-[#4b0082] hover:border-[#00f0ff]'}`}
                    >
                      Yes, I'm new
                    </Button>
                    <Button
                      type="button"
                      data-testid="new-patient-no"
                      onClick={() => handleSelectChange("is_new_patient", false)}
                      className={`rounded-full px-6 ${!formData.is_new_patient 
                        ? 'bg-[#00f0ff] text-black' 
                        : 'bg-[#2a1b3d] text-white border border-[#4b0082] hover:border-[#00f0ff]'}`}
                    >
                      Returning patient
                    </Button>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button 
                    onClick={prevStep}
                    data-testid="step-2-back"
                    variant="outline"
                    className="border-[#4b0082] text-white rounded-full px-8 py-6 hover:border-[#ff00ff] hover:text-[#ff00ff]"
                  >
                    <ChevronLeft className="mr-2 w-5 h-5" />
                    Back
                  </Button>
                  <Button 
                    onClick={nextStep}
                    data-testid="step-2-next"
                    className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] transition-all"
                  >
                    Next Step
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Submit */}
            {step === 3 && (
              <div data-testid="step-3-review" className="space-y-6 animate-fade-in-up">
                <h2 className="text-2xl font-bold text-white mb-6">Review & Submit</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="text-[#00f0ff] font-semibold uppercase tracking-wider text-sm">Personal Information</h3>
                    <div className="space-y-2 text-gray-300">
                      <p><span className="text-gray-500">Name:</span> {formData.first_name} {formData.last_name}</p>
                      <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                      <p><span className="text-gray-500">Phone:</span> {formData.phone}</p>
                      <p><span className="text-gray-500">DOB:</span> {formData.date_of_birth}</p>
                      {formData.insurance_provider && (
                        <p><span className="text-gray-500">Insurance:</span> {formData.insurance_provider}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-[#ff00ff] font-semibold uppercase tracking-wider text-sm">Appointment Details</h3>
                    <div className="space-y-2 text-gray-300">
                      <p><span className="text-gray-500">Service:</span> {formData.service}</p>
                      <p><span className="text-gray-500">Doctor:</span> {formData.doctor || "Any available"}</p>
                      <p><span className="text-gray-500">Date:</span> {formData.preferred_date && format(formData.preferred_date, "MMMM d, yyyy")}</p>
                      <p><span className="text-gray-500">Time:</span> {formData.preferred_time}</p>
                      <p><span className="text-gray-500">Patient Type:</span> {formData.is_new_patient ? "New Patient" : "Returning Patient"}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="reason" className="text-gray-300 mb-2 block">Reason for Visit *</Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    data-testid="input-reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Please describe your symptoms or reason for this visit..."
                    rows={4}
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <Button 
                    onClick={prevStep}
                    data-testid="step-3-back"
                    variant="outline"
                    className="border-[#4b0082] text-white rounded-full px-8 py-6 hover:border-[#ff00ff] hover:text-[#ff00ff]"
                  >
                    <ChevronLeft className="mr-2 w-5 h-5" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    data-testid="submit-appointment"
                    disabled={isSubmitting}
                    className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? "Booking..." : "Confirm Appointment"}
                    <Check className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
